import {
  IconLayer,
  PathLayer,
  PolygonLayer,
  ScatterplotLayer,
} from '@deck.gl/layers';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { GOOGLE } from '@rsos/constants/mapProviders';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import usePrevious from '@rsos/utils/usePrevious';
import spriteMapping from '../assets/gis-search/sprites-output/sprites-info.json';
import gisSearchSprite from '../assets/gis-search/sprites-output/sprites_2x.png';
import layers from './storeLayers';

const HIGHLIGHT_LAYER_ID = 'gis-feature-highlight';
const HIGHLIGHT_FILL_COLOR = [64, 196, 255, 60];
const HIGHLIGHT_LINE_COLOR = [64, 196, 255, 220];
const FLASH_INTERVAL_MS = 300;
const FLASH_COUNT = 2;

const useGisSearchGL = ({ map, overlay, isMapIconDisabled }) => {
  const flashTimersRef = useRef([]);
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const selectedLocation = useSelector(
    state => state.gisSearch.selectedLocation,
  );

  const highlightedFeature = useSelector(
    state => state.gisSearch.highlightedFeature,
  );

  const prevSelectedLocation = usePrevious(selectedLocation);
  const prevHighlightedFeature = usePrevious(highlightedFeature);

  const LAYER_IDS = {
    GIS_SEARCH_DEFAULT: 'gis-search-default',
  };

  const MARKER_SIZES = {
    sizeScale: 130, // this is the factor to scale the icon.
    sizeMinPixels: 30, // when zoomed out, the max height of the icon.
    sizeMaxPixels: 45, // when zoomed in, the max height of the icon.
  };

  const layerFactories = {
    [LAYER_IDS.GIS_SEARCH_DEFAULT]: (data, extraProps = {}) => {
      return new IconLayer({
        id: LAYER_IDS.GIS_SEARCH_DEFAULT,
        data,
        getPosition: d => [d.location.x, d.location.y],
        pickable: !isMapIconDisabled,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 60],
        iconAtlas: gisSearchSprite,
        iconMapping: spriteMapping,
        sizeUnits: 'meters',
        ...MARKER_SIZES,
        getIcon: () => 'gis_search', // matches entry in iconMapping
        onHover: () => {
          // do nothing
        },

        onClick: () => {
          // do nothing
        },

        ...extraProps,
      });
    },
  };
  useEffect(() => {
    if (overlay && selectedLocation) {
      layers.save(
        layerFactories[LAYER_IDS.GIS_SEARCH_DEFAULT]([selectedLocation], {
          visible: true,
        }),
      );
      overlay.setLayers(layers.getAll());
      const viewOption = {
        center: [selectedLocation.location.x, selectedLocation.location.y],
      };
      if (!prevSelectedLocation && selectedLocation) {
        panMapTo({
          mapProvider,
          mapInstance: map.mapInstance,
          latitude: viewOption.center[1],
          longitude: viewOption.center[0],
          zoom: mapProvider === GOOGLE ? 16 : 18,
        });
      }
    } else if (overlay && !selectedLocation && prevSelectedLocation) {
      layers.save(
        layerFactories[LAYER_IDS.GIS_SEARCH_DEFAULT]([prevSelectedLocation], {
          visible: false,
        }),
      );
      overlay.setLayers(layers.getAll());
      /*
        current implementation has requirement to not change viewport when marker is removed
        add useCenterPoint and uncomment below code to recenter to jurisdiction/call's center when marker is removed
        const center = useCenterPoint();
        map.mapInstance.view.goTo({
          center: [center.longitude, center.latitude],
        });
      */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, selectedLocation, isMapIconDisabled]);

  useEffect(() => {
    if (!overlay) return;

    if (highlightedFeature) {
      flashTimersRef.current.forEach(id => clearTimeout(id));
      flashTimersRef.current = [];

      const { centroid, type, rings, paths } = highlightedFeature;

      panMapTo({
        mapProvider,
        mapInstance: map.mapInstance,
        latitude: centroid.latitude,
        longitude: centroid.longitude,
        zoom: mapProvider === GOOGLE ? 17 : 18,
      });

      if (type === 'polygon' && rings?.length) {
        const highlightLayer = new PolygonLayer({
          id: HIGHLIGHT_LAYER_ID,
          data: [{ polygon: rings }],
          getPolygon: d => d.polygon,
          pickable: false,
          filled: true,
          stroked: true,
          getFillColor: HIGHLIGHT_FILL_COLOR,
          getLineColor: HIGHLIGHT_LINE_COLOR,
          lineWidthMinPixels: 3,
          lineWidthMaxPixels: 6,
        });
        layers.save(highlightLayer);
      } else if (type === 'polyline' && paths?.length) {
        const pathData = paths.map(path => ({ path }));
        const highlightLayer = new PathLayer({
          id: HIGHLIGHT_LAYER_ID,
          data: pathData,
          getPath: d => d.path,
          pickable: false,
          widthUnits: 'pixels',
          getWidth: 3,
          getColor: HIGHLIGHT_LINE_COLOR,
          capRounded: true,
          jointRounded: true,
        });
        layers.save(highlightLayer);
      } else if (type === 'point' && centroid) {
        const position = [centroid.longitude, centroid.latitude];

        const setVisible = visible => {
          const layer = new ScatterplotLayer({
            id: HIGHLIGHT_LAYER_ID,
            data: [{ position }],
            getPosition: d => d.position,
            pickable: false,
            filled: false,
            stroked: true,
            visible,
            getLineColor: HIGHLIGHT_LINE_COLOR,
            getRadius: 20,
            radiusUnits: 'pixels',
            lineWidthMinPixels: 3,
          });
          layers.save(layer);
          overlay.setLayers(layers.getAll());
        };

        // Flash sequence: on → off → on → off → remove
        const totalSteps = FLASH_COUNT * 2;
        for (let i = 0; i < totalSteps; i++) {
          const timerId = setTimeout(
            () => setVisible(i % 2 === 0),
            i * FLASH_INTERVAL_MS,
          );
          flashTimersRef.current.push(timerId);
        }

        const removeTimerId = setTimeout(() => {
          layers.deleteLayer(HIGHLIGHT_LAYER_ID);
          overlay.setLayers(layers.getAll());
        }, totalSteps * FLASH_INTERVAL_MS);
        flashTimersRef.current.push(removeTimerId);

        return;
      } else {
        layers.deleteLayer(HIGHLIGHT_LAYER_ID);
      }
    } else if (!highlightedFeature && prevHighlightedFeature) {
      flashTimersRef.current.forEach(id => clearTimeout(id));
      flashTimersRef.current = [];
      layers.deleteLayer(HIGHLIGHT_LAYER_ID);
    }

    overlay.setLayers(layers.getAll());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, highlightedFeature]);
};

export const GisSearchGL = React.memo(({ map, overlay, isMapIconDisabled }) => {
  useGisSearchGL({ map, overlay, isMapIconDisabled });
  return <></>;
});
